import type { ApolloCache } from '@apollo/client';
import { Apollo } from 'src/gql/generated';

import type { SkillAttachmentChanges } from './skill-attachment-editor';

/**
 * Seeds the cache with the bytes a draft save just stored, so the open file shows without
 * being fetched again and a PDF preview does not reload.
 *
 * The author's files are cached by their hash, so a file the save only moved or renamed
 * already has its contents cached. A new PDF upload and an edited text file have new hashes:
 * their bytes are the ones the save sent.
 */
export function seedSavedSkillAttachments({
  cache,
  skill,
  submitted,
}: {
  cache: ApolloCache;
  skill: Pick<Apollo.AgentSkillSimpleFragment, 'id'> & {
    attachments: Pick<
      Apollo.AgentSkillAttachmentFieldsFragment,
      'contentType' | 'name' | 'path' | 'sha256'
    >[];
  };
  /** The changes the save sent. */
  submitted: SkillAttachmentChanges;
}): void {
  for (const attachment of skill.attachments) {
    const base64 = getSubmittedBase64({ attachment, submitted });
    if (base64 !== null)
      cache.writeQuery<
        Apollo.GetAgentSkillDraftAttachmentQuery,
        Apollo.GetAgentSkillDraftAttachmentQueryVariables
      >({
        data: {
          __typename: 'Query',
          agentSkillDraftAttachment: {
            __typename: 'AgentSkillAttachmentContent',
            base64,
            contentType: attachment.contentType,
            name: attachment.name,
          },
        },
        query: Apollo.GetAgentSkillDraftAttachmentDocument,
        variables: { sha256: attachment.sha256, skillId: skill.id },
      });
  }
}

function getSubmittedBase64({
  attachment,
  submitted,
}: {
  attachment: Pick<Apollo.AgentSkillAttachmentFieldsFragment, 'contentType' | 'path'>;
  submitted: SkillAttachmentChanges;
}): string | null {
  const upload = submitted.pdfAttachments?.find(file => {
    const folder = file.folderPath ?? 'references';
    return attachment.path === `${folder ? `${folder}/` : ''}${file.id}.pdf`;
  });
  if (upload && attachment.contentType === 'application/pdf') return upload.base64;
  const textFile = submitted.textFiles?.find(file => file.path === attachment.path);
  if (!textFile) return null;
  const bytes = new TextEncoder().encode(textFile.content);
  return btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join(''));
}
